import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { AutoAwesome, PlaylistAddCheck } from "@mui/icons-material";
import PaginationControls from "@/components/ui/PaginationControls";
import { useTablePagination } from "@/components/ui/useTablePagination";
import { formatBytes } from "@/pages/IncidentManagement/components/NewIncidentDialog/fileUtils";
import type { ReportGenReport } from "@/shared/interfaces/ReportGenReport";
import {
  MAX_RUN_POLICY_DOCS,
  MAX_RUN_POLICY_DOC_BYTES,
  MAX_RUN_REPORTS,
  type PolicyDoc,
  type StartComplianceRunRequest,
} from "@/features/stationAdmin/api/iaComplianceApi";
import { iaErrorMessage } from "@/features/stationAdmin/hooks/useIaPolicyDocs";
import { formatShortDate } from "./complianceView";

/** Display cap on the report picker; the run itself is capped at 20. */
const MAX_PICKER_REPORTS = 200;

interface RunComplianceCardProps {
  docs: PolicyDoc[];
  docsLoading: boolean;
  docsError: boolean;
  onRetryDocs: () => void;
  /** Recent reports, newest first (the card applies the display cap). */
  reports: ReportGenReport[];
  reportsLoading: boolean;
  reportsError: boolean;
  onRetryReports: () => void;
  onStartRun: (req: StartComplianceRunRequest) => void;
  isStarting: boolean;
  startError: unknown;
}

/**
 * Compliance-run composer: pick policy docs (default all, ≤10, combined ≤20MB)
 * and up to 20 recent reports, then start an AI check. The server re-validates
 * every cap — these gates exist for immediate feedback, not enforcement.
 */
export const RunComplianceCard: React.FC<RunComplianceCardProps> = ({
  docs,
  docsLoading,
  docsError,
  onRetryDocs,
  reports,
  reportsLoading,
  reportsError,
  onRetryReports,
  onStartRun,
  isStarting,
  startError,
}) => {
  const theme = useTheme();

  // null = "the default selection" (all docs, up to the cap). Materialized on
  // the first user toggle. Stale ids (a doc deleted after selection) are
  // filtered against the current list rather than silently sent.
  const [docSelection, setDocSelection] = useState<string[] | null>(null);
  const availableDocIds = useMemo(
    () => new Set(docs.map((d) => d.docId)),
    [docs],
  );
  const selectedDocIds = useMemo(() => {
    const base =
      docSelection ?? docs.slice(0, MAX_RUN_POLICY_DOCS).map((d) => d.docId);
    return base.filter((id) => availableDocIds.has(id));
  }, [docSelection, docs, availableDocIds]);

  const toggleDoc = (docId: string) => {
    const next = new Set(selectedDocIds);
    if (next.has(docId)) {
      next.delete(docId);
    } else {
      if (next.size >= MAX_RUN_POLICY_DOCS) return;
      next.add(docId);
    }
    setDocSelection([...next]);
  };

  const selectedDocs = docs.filter((d) => selectedDocIds.includes(d.docId));
  const selectedDocBytes = selectedDocs.reduce((sum, d) => sum + d.sizeBytes, 0);
  const overDocBytes = selectedDocBytes > MAX_RUN_POLICY_DOC_BYTES;

  const displayReports = useMemo(
    () => reports.slice(0, MAX_PICKER_REPORTS),
    [reports],
  );
  const [rawSelectedReportIds, setRawSelectedReportIds] = useState<string[]>(
    [],
  );
  // A refetch can push previously-selected reports out of the display window
  // (newest-first slice). Prune the selection against the CURRENT window so
  // the count label, cap math, canRun gate, and the request body all agree —
  // never silently check fewer reports than the UI claims.
  const displayReportIds = useMemo(
    () => new Set(displayReports.map((r) => r.id)),
    [displayReports],
  );
  const selectedReportIds = useMemo(
    () => rawSelectedReportIds.filter((id) => displayReportIds.has(id)),
    [rawSelectedReportIds, displayReportIds],
  );
  const selectedReportSet = useMemo(
    () => new Set(selectedReportIds),
    [selectedReportIds],
  );

  const toggleReport = (reportId: string) => {
    const prev = selectedReportIds;
    if (prev.includes(reportId)) {
      setRawSelectedReportIds(prev.filter((id) => id !== reportId));
    } else if (prev.length < MAX_RUN_REPORTS) {
      setRawSelectedReportIds([...prev, reportId]);
    }
  };

  const selectNewest = () => {
    setRawSelectedReportIds(
      displayReports.slice(0, MAX_RUN_REPORTS).map((r) => r.id),
    );
  };

  const { page, rowsPerPage, setPage, setRowsPerPage, start, end } =
    useTablePagination(displayReports.length, { initialRowsPerPage: 10 });
  const pageReports = displayReports.slice(start, end);

  const canRun =
    !isStarting &&
    selectedReportIds.length > 0 &&
    selectedDocIds.length > 0 &&
    !overDocBytes;

  const handleRun = () => {
    if (!canRun) return;
    const byId = new Map(displayReports.map((r) => [r.id, r]));
    const req: StartComplianceRunRequest = {
      reports: selectedReportIds
        .map((id) => byId.get(id))
        .filter((r): r is ReportGenReport => !!r)
        .map((r) => ({
          reportId: r.id,
          title: r.reportName || r.details || undefined,
          // GET /reports items carry narrativeOnly, but ReportGenReport
          // doesn't declare it — read defensively rather than editing the
          // shared interface.
          narrativeOnly:
            (r as { narrativeOnly?: boolean }).narrativeOnly ?? false,
        })),
      policyDocIds: selectedDocIds,
    };
    onStartRun(req);
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Run Compliance Check
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Check recent reports against your policy documents
            </Typography>
          </Box>
          <Chip
            size="small"
            icon={<AutoAwesome sx={{ fontSize: 14 }} />}
            label="AI-generated"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              fontWeight: 600,
            }}
          />
        </Box>

        <Typography variant="overline" sx={{ display: "block", mt: 2 }}>
          Policies ({selectedDocIds.length} of {docs.length} selected
          {docs.length > 0 ? ` · ${formatBytes(selectedDocBytes)}` : ""})
        </Typography>
        {docsError ? (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={onRetryDocs}>
                Retry
              </Button>
            }
          >
            Failed to load policy documents.
          </Alert>
        ) : docsLoading ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Skeleton variant="rounded" height={28} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" height={28} sx={{ borderRadius: 1 }} />
          </Stack>
        ) : docs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Upload a policy document to enable compliance checks.
          </Typography>
        ) : (
          <Stack sx={{ maxHeight: 168, overflowY: "auto" }}>
            {docs.map((doc) => {
              const checked = selectedDocIds.includes(doc.docId);
              return (
                <FormControlLabel
                  key={doc.docId}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleDoc(doc.docId)}
                      // At the doc cap, unchecked boxes are dead — disable them
                      // so the no-op is visible.
                      disabled={
                        !checked &&
                        selectedDocIds.length >= MAX_RUN_POLICY_DOCS
                      }
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {doc.fileName}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        ({formatBytes(doc.sizeBytes)})
                      </Typography>
                    </Typography>
                  }
                />
              );
            })}
          </Stack>
        )}
        {overDocBytes && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Selected policies exceed the 20MB combined limit — deselect some to
            run a check.
          </Alert>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Typography variant="overline">
            Reports ({selectedReportIds.length} of {MAX_RUN_REPORTS} max, last
            30 days)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<PlaylistAddCheck fontSize="small" />}
              onClick={selectNewest}
              disabled={reportsLoading || displayReports.length === 0}
            >
              Select newest {Math.min(MAX_RUN_REPORTS, displayReports.length) || MAX_RUN_REPORTS}
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => setRawSelectedReportIds([])}
              disabled={selectedReportIds.length === 0}
            >
              Clear
            </Button>
          </Stack>
        </Stack>

        {reportsError ? (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={onRetryReports}>
                Retry
              </Button>
            }
          >
            Failed to load recent reports.
          </Alert>
        ) : reportsLoading ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Skeleton variant="rounded" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" height={36} sx={{ borderRadius: 1 }} />
          </Stack>
        ) : displayReports.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontStyle: "italic" }}
          >
            No reports in the last 30 days.
          </Typography>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="Recent reports">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Modified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageReports.map((report) => {
                    const checked = selectedReportSet.has(report.id);
                    const atCap =
                      !checked && selectedReportIds.length >= MAX_RUN_REPORTS;
                    return (
                      <TableRow
                        key={report.id}
                        hover
                        onClick={() => !atCap && toggleReport(report.id)}
                        selected={checked}
                        sx={{ cursor: atCap ? "default" : "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={atCap}
                            inputProps={{
                              "aria-label": `Select ${
                                report.reportName || report.id
                              }`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleReport(report.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="body2" noWrap>
                            {report.reportName || report.details || report.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={report.status}
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatShortDate(
                              report.dateModified || report.reportedByDate,
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ mt: 4 }}>
              <PaginationControls
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalItems={displayReports.length}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                itemLabel="reports"
                hideWhenEmpty
              />
            </Box>
          </>
        )}

        {startError != null && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {iaErrorMessage(
              startError,
              "Couldn't start the compliance check. Please retry.",
            )}
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Typography variant="caption" color="text.secondary">
            AI checks are advisory — verify findings against the policy text.
          </Typography>
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={!canRun}
            startIcon={
              isStarting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesome fontSize="small" />
              )
            }
          >
            {isStarting ? "Starting…" : "Run check"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
